import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { selectAllId, WITHOUT_DEPARTMENTS_ID } from 'pages/tasks/constants';
import type { TreeNode } from 'types/interfaces';

import { buildDeptFilterValue } from '../lib/distributionHelpers';

const isDistributableDepartment = (node: TreeNode) =>
  node.key !== selectAllId &&
  node.key !== WITHOUT_DEPARTMENTS_ID &&
  node.key.trim() !== '';

/**
 * Участок с главной подставляется, только когда он там ровно один.
 * Несколько участков, ноль или «Выбрать все» открывают окно с пустым пикером.
 */
export function pickPrefilledDepartment(nodes: TreeNode[]) {
  return nodes.length === 1 && isDistributableDepartment(nodes[0]) ? nodes : [];
}

export function useDistributionDeptSelection({
  visible,
  defaultSelection,
  treeValue
}: {
  visible: boolean;
  defaultSelection: TreeNode[];
  treeValue: TreeNode[];
}) {
  const [selection, setSelection] = useState<TreeNode[]>([]);
  const wasVisible = useRef(false);

  useEffect(() => {
    if (visible && !wasVisible.current) {
      setSelection(pickPrefilledDepartment(defaultSelection));
    }
    wasVisible.current = visible;
  }, [visible, defaultSelection]);

  const deptValueForApi = useMemo(
    () => buildDeptFilterValue(selection, treeValue),
    [selection, treeValue]
  );

  /**
   * Единственное место, где выбор урезается до одного участка:
   * в отличие от pickPrefilledDepartment здесь оставляем первый выбранный,
   * а не сбрасываем весь выбор.
   */
  const handleSelectionChange = useCallback((next: TreeNode[]) => {
    setSelection(next.filter(isDistributableDepartment).slice(0, 1));
  }, []);

  return {
    selection,
    deptValueForApi,
    handleSelectionChange
  };
}
